import * as React from 'react';
import { Box, BoxProps, Button, Icon, Image, Typography } from '@drone-js/core';
import { useIsCitiWhitelabel } from '@contexts/app/hooks/useIsCitiWhitelabel';
import { Logo } from '@components/Logo/Logo';
import { useIsMobile } from '@contexts/app/hooks/useIsMobile';
import styled from 'styled-components';

export interface HeaderProps {
    header: string;
    isTransparent?: boolean;
    showLogo?: boolean;
    buttonIcon?: React.FunctionComponentElement<{ size: string; color: number; className?: string }>;
    fontColor: 0 | 800;
    onButtonClick?: () => void;
    customLogo?: string;
}

interface WrapperBoxProps extends BoxProps {
    isTransparent?: boolean;
    isCitiWl?: boolean;
}

const WrapperBox = styled(Box)<WrapperBoxProps>(({ isTransparent, isCitiWl }) => ({
    backgroundColor: isTransparent ? 'transparent' : '#ffffff',
    height: '88px',
    position: 'relative',
    borderBottom: isCitiWl ? '4px solid #002D72' : undefined,
}));

const StyledBox = styled(Box)(() => ({
    minHeight: '44px !important',
}));

export const Header: React.FC<HeaderProps> = props => {
    const { isTransparent, fontColor, customLogo } = props;
    const isCitiWl = useIsCitiWhitelabel();
    const responsivePadding = useIsMobile() ? '4px' : '36px';
    const textColor = fontColor === 0 ? 'base.inverted' : 'base.default';

    return isCitiWl ? (
        <WrapperBox data-cy="header-wrapped" width="100%" boxShadow="bottom-s">
            <Box display="flex" height="100%" justifyContent="space-between">
                <Box width={'25%'}>
                    {
                        // tslint:disable-next-line: jsx-no-multiline-js
                        props.showLogo ? (
                            <Logo />
                        ) : (
                            props.buttonIcon && (
                                <Button
                                    size="s"
                                    variant="icon"
                                    leadingIcon="fill.arrow.thin-back"
                                    onClick={props.onButtonClick}
                                    data-cy="header-button-element"
                                />
                            )
                        )
                    }
                </Box>
                <Box>
                    <Box textAlign="center" alignContent="center">
                        <Typography variant="caption-default-strong" textColor={textColor}>
                            {props.header}
                        </Typography>
                    </Box>
                </Box>
                <Box width="25%" />
            </Box>
        </WrapperBox>
    ) : (
        <StyledBox
            height="44px"
            alignContent="center"
            px="12"
            display="flex"
            justifyContent="flex-start"
            backgroundColor={isTransparent ? 'base.transparent' : 'base.surface'}
            data-cy="header"
        >
            <Box alignContent="center" width={'10%'}>
                {props.buttonIcon && (
                    <Box
                        as="button"
                        onClick={props.onButtonClick}
                        data-cy="header-button-element"
                        alignContent="center"
                    >
                        <Icon label="Go back to previous page button" size="20" id="fill.arrow.thin-back" />
                    </Box>
                )}
            </Box>
            <Box width="80%" textAlign="center" alignContent="center">
                {!!customLogo ? (
                    <Image maxWidth="50%" maxHeight="50%" alt="service logo" src={customLogo} />
                ) : (
                    <Typography variant="body-default-strong" textColor={textColor}>
                        {props.header}
                    </Typography>
                )}
            </Box>
        </StyledBox>
    );
};

Header.displayName = 'Header';
